import { AuthService } from './authService';

export interface BosonCustomVoice {
  voiceId: string;
  title: string;
  refText: string;
  createdAt: string;
}

export interface ServerSettingsSummary {
  googlePlaces: {
    configured: boolean;
    source: 'server' | 'env' | 'none';
    maskedKey: string;
  };
  elevenLabs?: {
    configured: boolean;
    source: 'server' | 'env' | 'none';
    maskedKey: string;
    voiceId: string;
  };
  boson?: {
    configured: boolean;
    source: 'server' | 'env' | 'none';
    maskedKey: string;
  };
  bosonCustomVoices?: BosonCustomVoice[];
  deepgram?: {
    configured: boolean;
    source: 'server' | 'env' | 'none';
    maskedKey: string;
  };
  engines?: {
    voiceRuntimeMode: 'auto' | 'mac-local' | 'pc-nvidia' | 'vps-gpu';
    preferredLocalTts: 'supertonic' | 'voxcpm' | 'boson-higgs' | 'elevenlabs';
    supertonicBaseUrl: string;
    supertonicVoiceStyle: string;
    supertonicCharacter: 'business' | 'free';
    voxcpmBaseUrl: string;
    voxcpmAuthConfigured: boolean;
    maskedVoxcpmAuthToken: string;
  };
}

export interface GooglePlacesTestResult {
  ok: boolean;
  status?: string;
  error?: string;
  candidate?: {
    name?: string;
    formatted_address?: string;
  } | null;
}

const readJson = async <T>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || `Server settings request mislukt (${response.status}).`);
  }
  return payload as T;
};

export class ServerSettingsService {
  static async getSettings(): Promise<ServerSettingsSummary> {
    const response = await AuthService.authFetch('/api/settings/server');
    return readJson<ServerSettingsSummary>(response);
  }

  static async saveGooglePlacesKey(apiKey: string): Promise<ServerSettingsSummary> {
    const response = await AuthService.authFetch('/api/settings/server/google-places', {
      method: 'PUT',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({ apiKey }),
    });
    const payload = await readJson<{ settings: ServerSettingsSummary }>(response);
    return payload.settings;
  }

  static async clearGooglePlacesKey(): Promise<ServerSettingsSummary> {
    const response = await AuthService.authFetch('/api/settings/server/google-places', {
      method: 'DELETE',
    });
    const payload = await readJson<{ settings: ServerSettingsSummary }>(response);
    return payload.settings;
  }

  static async testGooglePlacesKey(apiKey = ''): Promise<GooglePlacesTestResult> {
    const response = await AuthService.authFetch('/api/settings/server/google-places/test', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({ apiKey }),
    });
    return readJson<GooglePlacesTestResult>(response);
  }

  static async getVoiceSettings(): Promise<Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'boson' | 'bosonCustomVoices'>>> {
    const response = await AuthService.authFetch('/voice/stack/settings/server');
    return readJson<Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'boson' | 'bosonCustomVoices'>>>(response);
  }

  static async saveElevenLabs(input: { apiKey?: string; voiceId?: string }) {
    const response = await AuthService.authFetch('/voice/stack/settings/server/elevenlabs', {
      method: 'PUT',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async clearElevenLabs() {
    const response = await AuthService.authFetch('/voice/stack/settings/server/elevenlabs', { method: 'DELETE' });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async saveDeepgram(apiKey: string) {
    const response = await AuthService.authFetch('/voice/stack/settings/server/deepgram', {
      method: 'PUT',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({ apiKey }),
    });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async clearDeepgram() {
    const response = await AuthService.authFetch('/voice/stack/settings/server/deepgram', { method: 'DELETE' });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async saveVoiceEngines(input: {
    voiceRuntimeMode: 'auto' | 'mac-local' | 'pc-nvidia' | 'vps-gpu';
    preferredLocalTts: 'supertonic' | 'voxcpm' | 'boson-higgs' | 'elevenlabs';
    supertonicBaseUrl?: string;
    supertonicVoiceStyle?: string;
    supertonicCharacter?: 'business' | 'free';
    voxcpmBaseUrl?: string;
    voxcpmAuthToken?: string;
    bosonApiKey?: string;
  }) {
    const response = await AuthService.authFetch('/voice/stack/settings/server/engines', {
      method: 'PUT',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async getVoiceEngineStatus() {
    const response = await AuthService.authFetch('/voice/stack/engines/status');
    return readJson<{
      runtime: {
        voiceRuntimeMode: string;
        preferredLocalTts: string;
        selectedProvider: string;
      };
      supertonic: { configured: boolean; healthy: boolean; latencyMs: number; error?: string };
      voxcpm: { configured: boolean; healthy: boolean; latencyMs: number; error?: string };
      bosonHiggs: { configured: boolean; healthy: boolean; latencyMs: number; error?: string };
      elevenLabs: { configured: boolean; healthy: boolean; source: string };
      deepgram: { configured: boolean; healthy: boolean };
    }>(response);
  }

  static async saveBoson(input: { apiKey: string }) {
    const response = await AuthService.authFetch('/voice/stack/settings/server/boson', {
      method: 'PUT',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'boson' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  static async clearBoson() {
    const response = await AuthService.authFetch('/voice/stack/settings/server/boson', { method: 'DELETE' });
    const payload = await readJson<{ settings: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'boson' | 'bosonCustomVoices'>> }>(response);
    return payload.settings;
  }

  // Stap 2: custom voice kloon workflow
  static async createBosonVoice(input: { refAudio: string; refText: string; title?: string }): Promise<{ voiceId: string; title: string; stored: BosonCustomVoice }> {
    const response = await AuthService.authFetch('/voice/stack/boson/voices', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const retryAfterMs = payload?.retryAfterMs as number | undefined;
      const err: any = new Error(payload?.error || 'Boson voice create mislukt.');
      err.code = payload?.code;
      err.retryAfterMs = retryAfterMs;
      err.status = response.status;
      throw err;
    }
    return readJson(response);
  }

  static async listBosonVoices(): Promise<{ voices: BosonCustomVoice[] }> {
    const response = await AuthService.authFetch('/voice/stack/boson/voices');
    return readJson(response);
  }

  static async deleteBosonVoice(voiceId: string): Promise<{ success: boolean; voices: BosonCustomVoice[] }> {
    const response = await AuthService.authFetch(`/voice/stack/boson/voices/${encodeURIComponent(voiceId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || 'Voice verwijderen mislukt.');
    }
    return readJson(response);
  }

  static async runSupertonicAction(action: 'check' | 'setup' | 'start' | 'stop' | 'restart' | 'status') {
    const response = await AuthService.authFetch(`/voice/stack/supertonic/${action}`, {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson<{
      ok: boolean;
      action: string;
      stdout: string;
      stderr: string;
      settings?: Required<Pick<ServerSettingsSummary, 'elevenLabs' | 'deepgram' | 'engines' | 'bosonCustomVoices'>>;
    }>(response);
  }
}
